import React, { useEffect, useState } from "react";
import '../Style/EnquiryList.css';
import judicialLogo from '../Images/judicialLogo.png';
import { useDispatch, useSelector } from "react-redux";
import { createEnquiryResponse, getAllEnquiries, openViewEnquiryDialog, selectedEnquiries } from "../Redux/Dashboard/DashboardSlice";
import Spinner from "../Spinner/Spinner";
import { Notify } from "../CommonPopup/Popup";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { IoCloseCircleOutline } from "react-icons/io5";

const EnquiryList = () => {
    const dispatch = useDispatch();
    const selector = useSelector((s) => s.DashboardSlice);
    const getEnquiries = selector.getEnquiries || [];
    const loader = selector.loader;

    const [currentPage, setCurrentPage] = useState(1);
    const enquiryPerPage = 8;
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("Pending");
    const [showModal, setShowModal] = useState(false);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [responseText, setResponseText] = useState("");
    const [manualLoader, setManualLoader] = useState(false);

    useEffect(() => {
        dispatch(getAllEnquiries());
    }, [dispatch]);

    const filteredEnquiries = getEnquiries
        .filter((enquiry) => enquiry.status === activeTab)
        .filter((enquiry) => {
            const name = enquiry.user_id?.name || '';
            const email = enquiry.user_id?.email || '';
            const phone = enquiry.user_id?.phone || '';
            const category = enquiry.category || '';
            return (
                name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                phone.includes(searchTerm)
            );
        })
        .sort((a, b) => {
            if (activeTab === "Pending") {
                return new Date(b.created_at) - new Date(a.created_at); // Sort by created_at (latest first)
            } else {
                return new Date(b.responseUpdated) - new Date(a.responseUpdated); // Sort by responseUpdated (latest first)
            }
        }) || [];

    const totalPages = Math.ceil(filteredEnquiries.length / enquiryPerPage);
    const indexOfLastEnquiry = currentPage * enquiryPerPage;
    const indexOfFirstEnquiry = indexOfLastEnquiry - enquiryPerPage;
    const currentEnquiry = filteredEnquiries.slice(indexOfFirstEnquiry, indexOfLastEnquiry);

    const nextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const prevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleOpenModal = (enquiry) => {
        setSelectedEnquiry(enquiry);
        setResponseText(enquiry.response || "");
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setSelectedEnquiry(null);
        setResponseText("");
        setShowModal(false);
    };

    const handleSubmitResponse = () => {
        setManualLoader(true);
        dispatch(createEnquiryResponse({ enquiryId: selectedEnquiry?._id, response: responseText }))
            .unwrap()
            .then((data) => {
                Notify({ data: "success", msg: "Response Submitted Successfully", type: "success" });

                // Calculate the new filtered enquiries after response (excluding the responded enquiry)
                const newFilteredEnquiries = filteredEnquiries.filter(
                    (enquiry) => enquiry._id !== selectedEnquiry?._id
                );
                const newTotalPages = Math.ceil(newFilteredEnquiries.length / enquiryPerPage);

                // Check if the current page will be empty or invalid
                if (newFilteredEnquiries.length === 0) {
                    // If no enquiries left, reset to page 1
                    setCurrentPage(1);
                } else if (
                    currentPage > newTotalPages ||
                    newFilteredEnquiries.slice((currentPage - 1) * enquiryPerPage, currentPage * enquiryPerPage).length === 0
                ) {
                    // If current page is invalid or empty, go to the previous page
                    setCurrentPage((prev) => Math.max(prev - 1, 1));
                }
                // Else, stay on the current page (no need to change currentPage)

                dispatch(getAllEnquiries());
                handleCloseModal();
            })
            .catch((err) => {
                console.error('createEnquiryResponse error:', err);
                Notify({ data: "error", msg: "Failed to submit response", type: "error" });
            })
            .finally(() => {
                setManualLoader(false);
                dispatch(getAllEnquiries());
            });
    };

    return (
        <div className="enquiry-list-main">
            <img className="judicial-logo" src={judicialLogo} alt="Judicial Logo" />
            <h2 className="text-white">Enquiries List ({getEnquiries.length})</h2>

            <div className="controls">
                <div className="search-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name, email, phone, or category..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="search-input"
                    />
                </div>
            </div>

            <div className="tab-buttons">
                <button
                    className={`tab-button ${activeTab === "Pending" ? "active" : ""}`}
                    onClick={() => { setActiveTab("Pending"); setCurrentPage(1); }}
                >
                    Open
                </button>
                <button
                    className={`tab-button ${activeTab === "Resolved" ? "active" : ""}`}
                    onClick={() => { setActiveTab("Resolved"); setCurrentPage(1); }}
                >
                    Resolved
                </button>
            </div>
            
            <div className="table-container">
                <table className="enquiries-table">
                    <thead>
                        <tr>
                            <th>{activeTab === "Pending" ? "Created At" : "Response Updated At"}</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Category</th>
                            <th>Title</th>
                            <th>Enquiry File</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    {loader || manualLoader ? (
                        <tbody>
                            <tr>
                                <td colSpan="8">
                                    <div className="spinner-wrapper">
                                        <Spinner />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {currentEnquiry.length > 0 ? (
                                currentEnquiry.map((enquiry, index) => (
                                    <tr key={index}>
                                        <td>{activeTab === "Pending" ? (
                                            enquiry.created_at ? new Date(enquiry.created_at).toLocaleString() : '-'
                                        ) : (
                                            enquiry.responseUpdated ? new Date(enquiry.responseUpdated).toLocaleString() : '-'
                                        )}</td>
                                        <td>{enquiry.user_id?.name || '-'}</td>
                                        <td>{enquiry.user_id?.email || '-'}</td>
                                        <td>{enquiry.user_id?.phone || '-'}</td>
                                        <td>{enquiry.category || '-'}</td>
                                        <td>{enquiry.title || '-'}</td>
                                        <td>
                                            {enquiry.enquiryFile ? (
                                                <a href={enquiry.enquiryFile} target="_blank" rel="noopener noreferrer" className="file-link">
                                                    View File
                                                </a>
                                            ) : (
                                                "No File"
                                            )}
                                        </td>
                                        <td className="">
                                            <div className="action-btn-wrapper">
                                                {activeTab === "Pending" ? (
                                                    <>
                                                        <button
                                                            className="action-btn response-btn"
                                                            onClick={() => handleOpenModal(enquiry)}
                                                        >
                                                            Reply
                                                        </button>
                                                        <button
                                                            className="action-btn view-btn"
                                                            onClick={() => {
                                                                dispatch(selectedEnquiries(enquiry));
                                                                dispatch(openViewEnquiryDialog(true));
                                                            }}
                                                        >
                                                            View Details
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        className="action-btn view-btn"
                                                        onClick={() => {
                                                            dispatch(selectedEnquiries(enquiry));
                                                            dispatch(openViewEnquiryDialog(true));
                                                        }}
                                                    >
                                                        View Details
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="enquiry-no-data">
                                        No enquiries found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    )}
                </table>
            </div>

            {!(loader || manualLoader) && totalPages > 1 && (
                <div className="pagination">
                    <button onClick={prevPage} disabled={currentPage === 1}>
                        <FontAwesomeIcon icon={faChevronLeft} /> Prev
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button onClick={nextPage} disabled={currentPage === totalPages}>
                        Next <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            )}

            {showModal && selectedEnquiry && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>Respond to Enquiry</h5>
                            <IoCloseCircleOutline
                                size={28}
                                onClick={handleCloseModal}
                                style={{ cursor: "pointer", color: "#f44336" }}
                            />
                        </div>
                        <div className="modal-body">
                            <div className="enquiry-details-grid">
                                <div className="enquiry-detail-card">
                                    <h6>User</h6>
                                    <p>{selectedEnquiry.user_id?.name || '-'}</p>
                                </div>
                                <div className="enquiry-detail-card">
                                    <h6>Email</h6>
                                    <p>{selectedEnquiry.user_id?.email || '-'}</p>
                                </div>
                                <div className="enquiry-detail-card">
                                    <h6>Phone</h6>
                                    <p>{selectedEnquiry.user_id?.phone || '-'}</p>
                                </div>
                                <div className="enquiry-detail-card">
                                    <h6>Enquiry Created At</h6>
                                    <p>{selectedEnquiry.created_at ? new Date(selectedEnquiry.created_at).toLocaleString() : '-'}</p>
                                </div>
                                <div className="enquiry-detail-card">
                                    <h6>Category</h6>
                                    <p>{selectedEnquiry.category || '-'}</p>
                                </div>
                                <div className="enquiry-detail-card">
                                    <h6>Title</h6>
                                    <p>{selectedEnquiry.title || '-'}</p>
                                </div>
                                <div className="enquiry-detail-card full">
                                    <h6>Message</h6>
                                    <p>{selectedEnquiry.message || '-'}</p>
                                </div>
                                <div className="enquiry-detail-card full">
                                    <h6>File</h6>
                                    {selectedEnquiry.enquiryFile ? (
                                        (() => {
                                            const fileUrl = selectedEnquiry.enquiryFile;
                                            const fileExtension = fileUrl.split('.').pop().toLowerCase();

                                            if (["jpg", "jpeg", "png", "gif", "webp"].includes(fileExtension)) {
                                                return (
                                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                                                        <img
                                                            src={fileUrl}
                                                            alt="Enquiry File"
                                                            className="file-preview"
                                                        />
                                                    </a>
                                                );
                                            } else if (["mp4", "webm", "ogg"].includes(fileExtension)) {
                                                return (
                                                    <video controls className="file-preview">
                                                        <source src={fileUrl} type={`video/${fileExtension}`} />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                );
                                            } else if (fileExtension === "pdf") {
                                                return (
                                                    <object data={fileUrl} type="application/pdf" width="100%" height="400px">
                                                        <p>Unable to preview PDF. <a href={fileUrl} target="_blank" rel="noreferrer">Download</a></p>
                                                    </object>
                                                );
                                            } else {
                                                return (
                                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="file-link">
                                                        View / Download File
                                                    </a>
                                                );
                                            }
                                        })()
                                    ) : (
                                        <p>No file uploaded</p>
                                    )}
                                </div>
                            </div>
                            <div className="response-form">
                                <label><strong>Your Response</strong></label>
                                <textarea
                                    rows={5}
                                    value={responseText}
                                    onChange={(e) => setResponseText(e.target.value)}
                                    placeholder="Type your response here..."
                                    className="response-textarea"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={handleCloseModal}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleSubmitResponse}>
                                Submit Response
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnquiryList;